import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Link2, Loader2, Baby } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  parentId: string;
  parentName: string;
  nurseryId: string;
  allChildren: { id: string; full_name: string; parent_id: string | null }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export default function LinkChildrenDialog({ parentId, parentName, nurseryId, allChildren, open, onOpenChange, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    allChildren.filter((c) => c.parent_id === parentId).map((c) => c.id)
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setLoading(true);

    // Unlink children previously linked to this parent
    const prevLinked = allChildren.filter((c) => c.parent_id === parentId).map((c) => c.id);
    const toUnlink = prevLinked.filter((id) => !selectedIds.includes(id));
    const toLink = selectedIds.filter((id) => !prevLinked.includes(id));

    if (toUnlink.length > 0) {
      await supabase.from("children").update({ parent_id: null }).in("id", toUnlink).eq("nursery_id", nurseryId);
    }

    if (toLink.length > 0) {
      await supabase.from("children").update({ parent_id: parentId, parent_name: parentName }).in("id", toLink).eq("nursery_id", nurseryId);
    }
    setLoading(false);
    toast.success("تم تحديث ربط الأطفال بنجاح");
    onOpenChange(false);
    onSuccess();
  };

  const linked = selectedIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            ربط أطفال بـ {parentName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">حدد الأطفال المرتبطين بولي الأمر هذا:</p>
          <ScrollArea className="max-h-[300px] border rounded-lg">
            <div className="p-2 space-y-1">
              {allChildren.map((child) => {
                const linkedToOther = child.parent_id && child.parent_id !== parentId;
                return (
                  <label
                    key={child.id}
                    className={`flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors ${
                      linkedToOther ? "opacity-50" : "hover:bg-muted/50"
                    }`}
                  >
                    <Checkbox
                      checked={selectedIds.includes(child.id)}
                      onCheckedChange={() => !linkedToOther && toggle(child.id)}
                      disabled={!!linkedToOther}
                    />
                    <span className="text-sm text-foreground flex-1">{child.full_name}</span>
                    {linkedToOther && (
                      <Badge variant="outline" className="text-[10px]">مرتبط بآخر</Badge>
                    )}
                  </label>
                );
              })}
            </div>
          </ScrollArea>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{linked} طفل محدد</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>إلغاء</Button>
              <Button size="sm" className="gradient-hero border-0" onClick={handleSave} disabled={loading}>
                {loading && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
                حفظ
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
